import React, { useEffect, useState } from 'react';
import Dialog from '@components/Atoms/Dialog';
import Typography from '@components/Atoms/Typography';
import Box from '@components/Atoms/Box';
import { DateTimePicker } from '@mui/x-date-pickers';
import { addMinutesToDate, parseToLocalizedDate } from '@utils/datetime';
import { LockStrategy, Testlet } from '../../types';
import LockStrategyRadio from './LockStrategyRadio';

const UpdateConfirmationDialog = ({
  onCancel,
  onConfirm,
  open,
  testlet,
}: {
  onCancel: () => void;
  onConfirm: (autoLockDate: Date | null, lockStrategy: LockStrategy) => void;
  open: boolean;
  testlet: Testlet;
}) => {
  const [selectedLockDate, setSelectedLockDate] = useState<Date | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [lockStrategy, setLockStrategy] = useState<LockStrategy>(testlet?.lock_strategy || LockStrategy.USE_REMAINING_TIME);

  useEffect(() => {
    if (testlet && testlet.unlocked_at) {
      const unlockDateLocalized = parseToLocalizedDate(testlet.unlocked_at);
      const lockDate = addMinutesToDate(unlockDateLocalized, testlet.auto_lock_duration);
      setSelectedLockDate(lockDate);
      setLockStrategy(testlet?.lock_strategy);
    }
  }, [testlet]);

  return (
    <Dialog
      actions={[
        {
          buttonProps: {
            variant: 'outlined',
            dataTestId: 'cancel-exam-action-dialog',
          },
          label: 'Cancel',
          onClick: () => onCancel(),
        },
        {
          buttonProps: {
            variant: 'contained',
            dataTestId: 'confirm-exam-action-dialog',
            color: 'play',
          },
          label: 'Update',
          onClick: () => {
            onConfirm(selectedLockDate, lockStrategy);
          },
        },
      ]}
      open={open}
      onClose={onCancel}
      data-testid="testlet-setting-dialog"
      title="Update Test"
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Typography
          sx={{
            textAlign: 'start',
          }}
          variant="body1"
        >
          Auto-lock Date/Time:
        </Typography>
        <Typography sx={{ mt: -2 }}>
          <DateTimePicker
            label="Select Target Lock Date"
            value={selectedLockDate}
            timezone="system"
            onChange={(newDate) => setSelectedLockDate(newDate)}
            minDate={new Date(testlet?.unlocked_at ?? new Date())}
            format="MMMM dd, yyyy hh:mm a"
            open={datePickerOpen}
            onOpen={() => setDatePickerOpen(true)}
            onClose={() => setDatePickerOpen(false)}
            slotProps={{
              textField: {
                onClick: () => setDatePickerOpen(true),
                sx: { cursor: 'pointer' },
              },
              popper: {
                sx: {
                  'ul[aria-label="Select meridiem"]': {
                    '&::after': {
                      display: 'none',
                    },
                  },
                },
              },
            }}
          />
        </Typography>
      </Box>
      <LockStrategyRadio
        lockStrategy={lockStrategy}
        setLockStrategy={setLockStrategy}
      />
    </Dialog>
  );
};

export default UpdateConfirmationDialog;
